import { useTheme } from '@mui/material/styles';
import type { ColDef } from 'ag-grid-community';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Checkbox from '../common/Checkbox';
import ClientSideAgGrid from '../common/customAgGrid/ClientSideAgGrid';
import CustomAgMultiSelectFilter from '../common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../common/customAgGrid/CustomAgTextFilter';
import Modal from '../common/Modal';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

// Custom cell renderer for checkbox selection
const CheckboxCellRenderer = ({ data, selectedProducts, onProductSelect }: any) => {
  return (
    <Checkbox
      checked={selectedProducts.includes(data.id)}
      onChange={() => onProductSelect(data.id)}
    />
  );
};

// Custom cell renderer for status badge
const StatusCellRenderer = ({ data }: any) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const statusLabel = useMemo(() => {
    const status = data.status?.toLowerCase();
    if (status === 'active') return t('common.active');
    if (status === 'inactive') return t('common.inactive');
    return data.status;
  }, [data.status, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        // justifyContent: "center",
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: (theme.palette as any).productAddFlow.statusBadgeBg,
          color: (theme.palette as any).productAddFlow.statusBadgeText,
          px: 2,
          py: 0,
          height: '25px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 500,
        }}
      >
        {statusLabel}
      </Box>
    </Box>
  );
};

// Types defined locally for this component
interface LubricationChartProduct {
  id: number;
  productName: string;
  maker: string;
  category: string;
  type: string;
  unit: string;
  status: string;
}

interface ProductAddFlowStep1Props {
  onNext: (_step: number) => void;
  onClose: () => void;
  onActivateProducts: () => void;
  selectedProducts: number[];
  onProductSelect: (_productId: number) => void;
  hasSelectedProducts: boolean;
  filteredProducts: LubricationChartProduct[];
  vesselName?: string;
}

const ProductAddFlowStep1: React.FC<ProductAddFlowStep1Props> = ({
  onNext,
  onClose,
  onActivateProducts,
  selectedProducts,
  onProductSelect,
  hasSelectedProducts,
  filteredProducts,
  vesselName,
}) => {
  const [tableHeight, setTableHeight] = useState<number>(400);
  const theme = useTheme();
  const { t } = useTranslation();

  // Refs for measuring heights
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate table height based on available space
  useEffect(() => {
    const calculateHeight = () => {
      if (headerRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const headerHeight = headerRef.current.clientHeight;
        // Subtract header height and add some padding buffer (24px for spacing)
        const availableHeight = containerHeight - headerHeight - 24;
        setTableHeight(Math.max(availableHeight, 300)); // Minimum 300px
      }
    };

    // Calculate on mount and when window resizes
    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(calculateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  // Column definitions for AgGrid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'select',
        headerName: t('table.columns.select'),
        width: 90,
        maxWidth: 90,
        minWidth: 90,
        flex: 0,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => (
          <CheckboxCellRenderer
            data={params.data}
            selectedProducts={selectedProducts}
            onProductSelect={onProductSelect}
          />
        ),
      },
      {
        field: 'id',
        headerName: t('table.columns.id'),
        width: 100,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'productName',
        headerName: t('table.columns.productName'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'maker',
        headerName: t('table.columns.maker'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'category',
        headerName: t('table.columns.category'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        width: 100,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'unit',
        headerName: t('table.columns.unit'),
        width: 120,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        cellRenderer: StatusCellRenderer,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
    ],
    [selectedProducts, onProductSelect, t],
  );

  return (
    <Modal
      size="lg"
      open={true}
      onClose={onClose}
      title={t('productAddFlow.step1Title', { vesselName: vesselName || t('common.vessel') })}
      footer={
        <>
          <Button variant="red" onClick={onClose}>
            {t('buttons.cancel')}
          </Button>
          {hasSelectedProducts ? (
            <Button variant="outlinedBlue" onClick={onActivateProducts}>
              {t('productAddFlow.activateAndContinue')}
            </Button>
          ) : (
            <Button variant="outlinedBlue" onClick={() => onNext(2)}>
              {t('productAddFlow.checkMasterCatalogue')}
            </Button>
          )}
        </>
      }
    >
      <Box
        ref={containerRef}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box ref={headerRef} sx={{ mt: 4, mb: 3, ml: 1 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              color: (theme.palette as any).productAddFlow.heading,
              mb: 1,
            }}
          >
            {t('productAddFlow.inactiveProducts')}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: (theme.palette as any).productAddFlow.subheading,
              fontSize: '15px',
            }}
          >
            {t('productAddFlow.inactiveProductsDescPart1')}
            <br />
            {t('productAddFlow.inactiveProductsDescPart2')}
          </Typography>
        </Box>
        <Box sx={{ height: `${tableHeight}px`, flex: 1 }}>
          <ClientSideAgGrid
            columnDefs={columnDefs as any}
            rowData={filteredProducts}
            loading={false}
            className="product-add-flow-step1-grid"
            onCellClicked={(params: any) => {
              // Only toggle selection when clicking on non-checkbox cells
              // to avoid double-toggle (checkbox onChange + row click)
              if (params.data && params.data.id && params.column.colId !== 'select') {
                onProductSelect(params.data.id);
              }
            }}
            isInModal={true}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ProductAddFlowStep1;
